/**
 * The pickup and delivery location sections of the details screen.
 *
 * The two sections are the same form wired to a different half of the shipment,
 * so this holds the wiring once and picks the actions by context. It is the
 * only place that knows a saved location also carries dock and forklift
 * details, which have to follow the address whenever one is chosen.
 */

import type { LocationErrors } from "@/Shared/Locations/Validators/locationsErrorMessagesForLocation";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import type { UUID } from "@/Shared/types";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { useDispatch } from "react-redux";
import BookShipmentLocationSection2 from "../CustomComponents/BookShipmentLocationSection2";
import {
  newDeliveryLocation,
  newPickupLocation,
  setDeliveryLocation,
  setDeliveryLocationHasDock,
  setDeliveryLocationHasForklift,
  setEnteringNewDelivery,
  setEnteringNewPickup,
  setPickupLocation,
  setPickupLocationHasDock,
  setPickupLocationHasForklift,
  setSelectedDeliverySavedLocationId,
  setSelectedPickupSavedLocationId,
} from "@/Slices/bookShipmentSlice";

interface BookShipmentDetailsLocationSectionProps {
  context: LocationContext;
  location: AddLocationLocationType;
  selectedSavedLocationId: UUID | undefined;
  enteringNew: boolean;
  savedLocations: Array<SavedLocation>;
  defaultLocationId: UUID | undefined;
  errorMessages: LocationErrors;
  warningMessages: LocationErrors;
  forceValidation: boolean;
  index: number;
  disablePostalCodeEntry: boolean;
  pickupDate: string;
}

export function BookShipmentDetailsLocationSection(
  props: BookShipmentDetailsLocationSectionProps,
) {
  const dispatch = useDispatch();
  const { context, savedLocations, defaultLocationId } = props;
  const isPickup = context === LocationContext.Pickup;

  const setSelectedSavedLocationId = isPickup
    ? setSelectedPickupSavedLocationId
    : setSelectedDeliverySavedLocationId;
  const setLocation = isPickup ? setPickupLocation : setDeliveryLocation;
  const setEnteringNew = isPickup
    ? setEnteringNewPickup
    : setEnteringNewDelivery;
  const newLocation = isPickup ? newPickupLocation : newDeliveryLocation;
  const setLocationHasDock = isPickup
    ? setPickupLocationHasDock
    : setDeliveryLocationHasDock;
  const setLocationHasForklift = isPickup
    ? setPickupLocationHasForklift
    : setDeliveryLocationHasForklift;

  function applyLoadingCapabilities(savedLocation: SavedLocation) {
    if (savedLocation.locationHasDock !== undefined) {
      dispatch(setLocationHasDock(savedLocation.locationHasDock));
    }
    if (savedLocation.locationHasForklift !== undefined) {
      dispatch(setLocationHasForklift(savedLocation.locationHasForklift));
    }
  }

  /**
   * Picking the address book with nothing chosen yet: fall back to the default
   * location, then a favourite, then whatever is first.
   */
  function onSelectFromAddressBook() {
    function updateForId(savedLocationId: UUID) {
      const savedLocation = savedLocations.find(
        (sl) => sl.savedLocationId === savedLocationId,
      );

      if (savedLocation !== undefined) {
        dispatch(setEnteringNew(false));
        dispatch(setSelectedSavedLocationId(savedLocation.savedLocationId));
        dispatch(setLocation(savedLocation.location));

        if (isPickup) {
          // Pickup applies the values even when the saved location has none,
          // which clears whatever was set before; delivery leaves them alone.
          // The two have always differed — preserved rather than unified here.
          dispatch(setLocationHasDock(savedLocation.locationHasDock));
          dispatch(setLocationHasForklift(savedLocation.locationHasForklift));
        } else {
          applyLoadingCapabilities(savedLocation);
        }
      }
    }

    if (defaultLocationId !== undefined) {
      return updateForId(defaultLocationId);
    }

    const firstFavourite = savedLocations.filter((sl) => sl.isFavourite)[0];
    if (firstFavourite !== undefined) {
      return updateForId(firstFavourite.savedLocationId);
    }

    const firstSavedLocation = savedLocations[0];
    if (firstSavedLocation !== undefined) {
      return updateForId(firstSavedLocation.savedLocationId);
    }
  }

  return (
    <BookShipmentLocationSection2
      location={props.location}
      selectedSavedLocationId={props.selectedSavedLocationId}
      enteringNew={props.enteringNew}
      savedLocations={savedLocations}
      defaultLocationId={defaultLocationId}
      onSelectSavedLocation={function (savedLocationId, savedLocation) {
        dispatch(setSelectedSavedLocationId(savedLocationId));
        dispatch(setLocation(savedLocation));
        const matchingSavedLocation = savedLocations.find(
          (sl) => sl.savedLocationId === savedLocationId,
        );
        if (matchingSavedLocation) {
          applyLoadingCapabilities(matchingSavedLocation);
        }
      }}
      onUpdateLocation={function (location) {
        dispatch(setLocation(location));
      }}
      onNewLocation={function () {
        dispatch(setSelectedSavedLocationId(undefined));
        dispatch(newLocation());
        dispatch(setEnteringNew(true));
      }}
      onSelectFromAddressBook={onSelectFromAddressBook}
      errorMessages={props.errorMessages}
      warningMessages={props.warningMessages}
      forceValidation={props.forceValidation}
      index={props.index}
      id={isPickup ? "pickup-details" : "delivery-details"}
      context={context}
      disablePostalCodeEntry={props.disablePostalCodeEntry}
      pickupDate={props.pickupDate}
    />
  );
}
